import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Eye, SearchX, Users } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { exportCsv } from "@/shared/lib/exportCsv";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ConsumptionBar, CONSUMPTION_OPTIONS, consumptionText, reachesConsumption } from "../components/ConsumptionBar";
import { FilterCard } from "../components/FilterCard";
import { FormField, selectClass } from "../components/FormField";
import { SortableTableHead } from "../components/SortableTableHead";
import { TablePagination } from "../components/TablePagination";
import { TableToolbar } from "../components/TableToolbar";
import { useInsurerConsumption } from "../hooks/useInsurerConsumption";
import type { ConsumptionPerson } from "../api/claimsApi";

type Filters = { company: string; packageName: string; relation: string; status: string; province: string; gender: string; consumption: string };

const NO_FILTERS: Filters = { company: "", packageName: "", relation: "", status: "", province: "", gender: "", consumption: "" };
const NO_PACKAGE = "__none__";

/** Valeurs distinctes et triées d'un champ de la liste (options des filtres). */
function distinct(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((v): v is string => Boolean(v)))].sort((a, b) => a.localeCompare(b, "fr"));
}

function detailPath(person: ConsumptionPerson): string {
  return person.type === "employee" ? `/dashboard/employees/${person.id}` : `/dashboard/beneficiaries/dependant/${person.id}`;
}

/** Compte Assurance : tous les bénéficiaires (agents et ayants droit), chacun avec sa consommation individuelle. */
export function EnterpriseEmployeesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.employees.enterpriseList;
  const pageTitle = t.safecheck.nav.employeeList;
  const { people, loading, error } = useInsurerConsumption();

  const [tableSearch, setTableSearch] = useState("");
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [draft, setDraft] = useState<Filters>(NO_FILTERS);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const { sortKey, sortDirection, toggleSort } = useTableSort("name", "asc");

  const handleSort = (key: string) => {
    toggleSort(key);
    setPage(1);
  };

  const filterFields: Array<{ key: keyof Filters; label: string; all: string; options: Array<{ value: string; label: string }> }> = [
    { key: "company", label: "Entreprise", all: "Toutes les entreprises", options: distinct(people.map((p) => p.company)).map((v) => ({ value: v, label: v })) },
    {
      key: "packageName",
      label: "Package",
      all: "Tous les packages",
      options: [...distinct(people.map((p) => p.package)).map((v) => ({ value: v, label: v })), { value: NO_PACKAGE, label: "Sans package" }],
    },
    { key: "consumption", label: "Consommation", all: "Toutes", options: CONSUMPTION_OPTIONS },
    { key: "relation", label: "Relation", all: "Toutes les relations", options: distinct(people.map((p) => p.relation)).map((v) => ({ value: v, label: v })) },
    { key: "status", label: "Statut", all: "Tous les statuts", options: [{ value: "active", label: "Actif" }, { value: "inactive", label: "Inactif" }] },
    { key: "province", label: "Province", all: "Toutes les provinces", options: distinct(people.map((p) => p.province)).map((v) => ({ value: v, label: v })) },
    { key: "gender", label: "Genre", all: "Tous", options: [{ value: "M", label: "Homme" }, { value: "F", label: "Femme" }] },
  ];

  const filtered = useMemo(() => {
    const term = tableSearch.toLowerCase();
    return people.filter((person) => {
      if (filters.company && person.company !== filters.company) return false;
      if (filters.packageName === NO_PACKAGE && person.package) return false;
      if (filters.packageName && filters.packageName !== NO_PACKAGE && person.package !== filters.packageName) return false;
      if (filters.relation && person.relation !== filters.relation) return false;
      if (filters.status && person.status !== filters.status) return false;
      if (filters.province && person.province !== filters.province) return false;
      if (filters.gender && person.gender !== filters.gender) return false;
      if (!reachesConsumption(person.percent, filters.consumption)) return false;
      return !term || `${person.name} ${person.matricule} ${person.company_matricule} ${person.company}`.toLowerCase().includes(term);
    });
  }, [people, tableSearch, filters]);

  const sorted = useMemo(
    () =>
      sortByColumn(filtered, sortKey, sortDirection, (person, key) => {
        if (key === "name") return person.name;
        if (key === "relation") return person.relation;
        if (key === "company") return person.company;
        if (key === "package") return person.package ?? "";
        if (key === "consumption") return person.percent ?? -1;
        if (key === "status") return labels.statuses[person.status];
        return "";
      }),
    [filtered, sortKey, sortDirection, labels.statuses],
  );

  const start = sorted.length === 0 ? 0 : (Math.min(page, Math.max(1, Math.ceil(sorted.length / pageSize))) - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const activeCount = useMemo(() => people.filter((person) => person.status === "active").length, [people]);

  const resetTable = () => {
    setTableSearch("");
    setFilters(NO_FILTERS);
    setDraft(NO_FILTERS);
    setPageSize(10);
    setPage(1);
  };

  const handleExport = () =>
    exportCsv(
      "assures.csv",
      ["Assuré", "Matricule", "Relation", "Entreprise", "Matricule entreprise", "Package", "Consommation", labels.columns.status],
      sorted.map((person) => [
        person.name,
        person.matricule,
        person.relation,
        person.company,
        person.company_matricule,
        person.package ?? "",
        consumptionText(person.consumed, person.ceiling, person.percent),
        labels.statuses[person.status],
      ]),
    );

  const columnDefs = [
    { key: "name", label: "Assuré", sortable: true },
    { key: "relation", label: "Relation", sortable: true },
    { key: "company", label: "Entreprise", sortable: true },
    { key: "package", label: "Package", sortable: true },
    { key: "consumption", label: "Consommation individuelle", sortable: true },
    { key: "status", label: labels.columns.status, sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ] as const;

  return (
    <>
      <Breadcrumbs homeHref="/dashboard/enterprise" items={[{ label: t.safecheck.nav.employeeManagement }, { label: pageTitle }]} />

      {error ? <p className="mb-3 rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : null}

      <FilterCard
        title="Filtres"
        onFilter={() => {
          setFilters(draft);
          setPage(1);
        }}
        onReset={() => {
          setDraft(NO_FILTERS);
          setFilters(NO_FILTERS);
          setPage(1);
        }}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {filterFields.map((field) => (
            <FormField key={field.key} label={field.label}>
              <select
                value={draft[field.key]}
                onChange={(event) => {
                  const value = event.target.value;
                  setDraft((current) => ({ ...current, [field.key]: value }));
                }}
                className={selectClass}
              >
                <option value="">{field.all}</option>
                {field.options.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </FormField>
          ))}
        </div>
      </FilterCard>

      <div className="theme-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border px-5 py-5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground" aria-hidden="true">
              <Users size={20} strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <h1 className="text-balance text-lg font-semibold text-foreground">{pageTitle}</h1>
              <p className="text-sm text-muted-foreground">
                {loading ? "Chargement des assurés…" : `${people.length.toLocaleString("fr-FR")} bénéficiaire(s) · ${activeCount.toLocaleString("fr-FR")} actif(s)`}
              </p>
            </div>
          </div>
        </div>

        <TableToolbar
          search={tableSearch}
          onSearchChange={(value) => {
            setTableSearch(value);
            setPage(1);
          }}
          searchPlaceholder="Rechercher un nom, un matricule, une entreprise…"
          resultCount={sorted.length}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={t.safecheck.entries}
          onExport={handleExport}
          onReset={resetTable}
          resetLabel="Réinitialiser"
        />

        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead>
              <tr className="border-b border-lime-200/80 bg-[linear-gradient(90deg,#f7fcef_0%,#eef9dc_100%)] dark:border-white/10 dark:bg-[linear-gradient(90deg,#16302b_0%,#1b3a26_100%)]">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                    plain
                    className={column.key === "actions" ? "pr-5 text-right" : column.key === "name" ? "pl-5" : undefined}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((person) => {
                const isActive = person.status === "active";
                return (
                  <tr key={person.key} className="border-b border-border transition-colors duration-150 last:border-0 hover:bg-accent/10">
                    <td className="py-3 pl-5 pr-4">
                      <Link to={detailPath(person)} className="block truncate text-sm font-semibold text-foreground transition-colors hover:text-[#4d7c0f] dark:hover:text-accent">
                        {person.name}
                      </Link>
                      <span className="mt-0.5 inline-flex rounded-md bg-violet-100 px-1.5 py-px text-[11px] font-semibold tabular-nums text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                        {person.matricule}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold text-muted-foreground">{person.relation}</td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Building2 size={14} className="shrink-0 text-muted" aria-hidden="true" />
                        {person.company}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{person.package ?? <span className="text-xs text-muted">Sans package</span>}</td>
                    <td className="px-4 py-3">
                      <ConsumptionBar consumed={person.consumed} ceiling={person.ceiling} percent={person.percent} />
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          isActive
                            ? "inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                            : "inline-flex items-center gap-1.5 rounded-full bg-card-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground ring-1 ring-border"
                        }
                      >
                        <span className={isActive ? "h-1.5 w-1.5 rounded-full bg-emerald-500" : "h-1.5 w-1.5 rounded-full bg-slate-400"} aria-hidden="true" />
                        {isActive ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="py-3 pl-4 pr-5 text-right">
                      <Link
                        to={detailPath(person)}
                        aria-label={`Voir la fiche de ${person.name}`}
                        title="Voir la fiche"
                        className="inline-grid h-9 w-9 place-items-center rounded-xl border border-border bg-card text-muted-foreground transition-[background-color,border-color,color] duration-150 hover:border-accent hover:bg-accent hover:text-accent-foreground"
                      >
                        <Eye size={16} aria-hidden="true" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={columnDefs.length} className="px-4 py-14 text-center">
                    <div className="mx-auto flex max-w-sm flex-col items-center gap-3">
                      <span className="grid h-12 w-12 place-items-center rounded-2xl bg-card-muted text-muted" aria-hidden="true">
                        <SearchX size={22} />
                      </span>
                      <p className="text-sm font-medium text-foreground">{loading ? "Chargement…" : labels.empty}</p>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <TablePagination currentPage={page} pageSize={pageSize} totalItems={sorted.length} onPageChange={setPage} labels={labels.pagination} />
      </div>
    </>
  );
}
